import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useI18n } from "@/i18n";

export function Eyebrow({ children, className = "" }) {
  return (
    <span className={`font-mono-tech text-xs uppercase tracking-[0.25em] text-emerald-700 font-semibold ${className}`}>
      {children}
    </span>
  );
}

// Curved bottom edge that transitions a teal section into the paper background
export function BottomWave({ fill = "#FBFAF8" }) {
  return (
    <div className="absolute bottom-0 left-0 right-0 leading-[0] pointer-events-none" aria-hidden>
      <svg viewBox="0 0 1440 100" preserveAspectRatio="none" className="w-full h-[60px] sm:h-[90px]">
        <path d="M0,40 C360,110 1080,-20 1440,50 L1440,100 L0,100 Z" fill={fill} />
      </svg>
    </div>
  );
}

export function PageHero({ eyebrow, title, highlight, subtitle, crumb, children }) {
  const { t, localePath } = useI18n();
  const metaItems = [t("common.heroMetaBrand"), t("common.heroMetaServices"), t("common.heroMetaHours")];
  return (
    <section className="relative bg-[#0E3B3B] text-white pt-32 pb-28 sm:pb-32 overflow-hidden" data-testid="page-hero">
      <div className="absolute inset-0 opacity-[0.15] pointer-events-none"
        style={{ backgroundImage: "radial-gradient(circle at 80% 20%, rgba(16,185,129,0.5), transparent 45%)" }} />
      <div className="relative z-[2] max-w-[1400px] mx-auto px-5 sm:px-8">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          {crumb && (
            <div className="font-mono-tech text-[11px] uppercase tracking-[0.2em] text-emerald-300/80 mb-5">
              <Link to={localePath("/")} className="hover:text-emerald-200">{t("nav.home")}</Link>
              <span className="mx-2 text-white/30">/</span>
              <span className="text-white/70">{crumb}</span>
            </div>
          )}
          {eyebrow && <div className="mb-4"><span className="font-mono-tech text-xs uppercase tracking-[0.25em] text-emerald-400 font-semibold">{eyebrow}</span></div>}
          <h1 className="font-display font-extrabold tracking-tight leading-[1.02] text-4xl sm:text-5xl lg:text-6xl max-w-4xl">
            {title} {highlight && <span className="text-emerald-400">{highlight}</span>}
          </h1>
          {subtitle && <p className="mt-6 max-w-2xl text-base sm:text-lg text-white/75 leading-relaxed">{subtitle}</p>}
          {children && <div className="mt-8">{children}</div>}
        </motion.div>
      </div>
      <div className="absolute inset-x-0 bottom-[54px] sm:bottom-[78px] border-y border-white/10 bg-[#0A2C2C]/60 backdrop-blur-[2px]" data-testid="page-hero-meta">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-2.5 flex items-center justify-between gap-4 font-mono-tech text-[9px] sm:text-[10px] uppercase tracking-[0.2em] text-white/60">
          <span className="text-emerald-300">{metaItems[0]}</span>
          <span className="hidden md:inline">{metaItems[1]}</span>
          <span>{metaItems[2]}</span>
        </div>
      </div>
      <BottomWave />
    </section>
  );
}

export function SectionHeading({ eyebrow, title, highlight, center, className = "" }) {
  return (
    <div className={`${center ? "text-center mx-auto" : ""} max-w-3xl ${className}`}>
      {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
      <h2 className="mt-4 font-display font-extrabold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-[#0B0F14] leading-[1.05]">
        {title} {highlight && <span className="text-emerald-600">{highlight}</span>}
      </h2>
    </div>
  );
}

export const reveal = {
  hidden: { opacity: 0, y: 36 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] } }),
};
